import { AlertCircle, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

interface LimitBannerProps {
  current: number;
  max: number;
  type: "routes" | "stops" | "posts";
  onUpgrade?: () => void;
}

const labels = {
  routes: { singular: "route", plural: "routes" },
  stops: { singular: "stop", plural: "stops" },
  posts: { singular: "post", plural: "posts" }
};

export function LimitBanner({ current, max, type, onUpgrade }: LimitBannerProps) {
  const { t } = useI18n();
  const label = labels[type];
  const isNearLimit = current >= max - 1;
  const isAtLimit = current >= max;

  if (!isNearLimit && !isAtLimit) return null;

  return (
    <div 
      className={`flex items-center justify-between gap-4 px-4 py-3 rounded-lg ${
        isAtLimit 
          ? "bg-destructive/10 border border-destructive/20" 
          : "bg-primary/10 border border-primary/20"
      }`}
      data-testid={`banner-limit-${type}`}
    >
      <div className="flex items-center gap-3">
        <AlertCircle className={`h-5 w-5 flex-shrink-0 ${isAtLimit ? "text-destructive" : "text-primary"}`} />
        <div>
          <p className="text-sm font-medium">
            {isAtLimit 
              ? t("limitBanner.maxReached", { current, max, plural: label.plural })
              : t("limitBanner.nearLimit", { current, max, plural: label.plural })
            }
          </p>
          <p className="text-xs text-muted-foreground">
            {isAtLimit 
              ? t("limitBanner.upgradeUnlimited")
              : t("limitBanner.upgradeUnlimitedOptions")
            }
          </p>
        </div>
      </div>
      {onUpgrade && (
        <Button size="sm" onClick={onUpgrade} className="flex-shrink-0" data-testid={`button-upgrade-${type}`}>
          <Crown className="h-4 w-4 mr-1" />
          {t("header.tier.insider")}
        </Button>
      )}
    </div>
  );
}
